import { c as _c } from "react/compiler-runtime";
// Period selector shared by the dashboard and finances.
//
// One trigger, one popover. The presets used to sit in the header as a row of
// five buttons with a separate "Период" button beside them — that put the least
// interesting control (a fixed list) in the most expensive space. Everything
// now lives behind the single «Период» button, which shows the active range.
//
// Presets alone were rejected by the founder: «один, три, семь, и дальше любые
// даты выставь, год, 9 месяцев, 34 дня, 47 дней», so an arbitrary from/to
// period sits in the same panel, not in an advanced mode elsewhere.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarRange, Check, ChevronDown } from "lucide-react";
import { DASHBOARD_PRESETS, type DashboardRange } from "@/api";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { cn } from "@/lib/utils";
const isoDay = (date: Date) => {
  // Local calendar day: toISOString() would shift midnight into the previous
  // day on any positive UTC offset.
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};
const shortDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "2-digit",
  month: "short"
});
export function RangePicker(t0) {
  const $ = _c(41);
  const {
    value,
    onChange
  } = t0;
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef(null);
  const panel = useRef(null);
  const [shift, setShift] = useState(0);
  const today = new Date();
  const t1 = today.getTime() - 2592000000;
  let t2;
  let t3;
  if ($[0] !== t1 || $[1] !== value.from || $[2] !== value.kind) {
    const monthAgo = new Date(t1);
    t2 = useState;
    t3 = value.kind === "custom" ? value.from : isoDay(monthAgo);
    $[0] = t1;
    $[1] = value.from;
    $[2] = value.kind;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const [from, setFrom] = t2(t3);
  const [to, setTo] = useState(value.kind === "custom" ? value.to : isoDay(today));
  let t4;
  let t5;
  if ($[5] !== open) {
    t4 = () => {
      if (!open) {
        return;
      }
      const onPointerDown = event => {
        if (!container.current?.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      const onKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("mousedown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    };
    t5 = [open];
    $[5] = open;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== open) {
    t6 = () => {
      if (!open) {
        setShift(0);
        return;
      }
      let applied = 0;
      const fit = () => {
        const el = panel.current;
        if (!el) {
          return;
        }
        const rect = el.getBoundingClientRect();
        const left = rect.left - applied;
        const right = rect.right - applied;
        const vw = document.documentElement.clientWidth;
        let next = 0;
        if (left < 8) {
          next = 8 - left;
        } else {
          if (right > vw - 8) {
            next = vw - 8 - right;
          }
        }
        applied = next;
        setShift(next);
      };
      fit();
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    };
    t7 = [open];
    $[8] = open;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useLayoutEffect(t6, t7);
  const invalid = !from || !to || from > to;
  let t8;
  if ($[11] !== t || $[12] !== value.days || $[13] !== value.from || $[14] !== value.kind || $[15] !== value.to) {
    t8 = value.kind === "allTime" ? t("dashboard.range.allTime") : value.kind === "preset" ? t("dashboard.range.days", {
      count: value.days
    }) : `${shortDate(value.from)} — ${shortDate(value.to)}`;
    $[11] = t;
    $[12] = value.days;
    $[13] = value.from;
    $[14] = value.kind;
    $[15] = value.to;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const label = t8;
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setOpen(_temp);
    t10 = <CalendarRange className="size-4" />;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  const t11 = open && "rotate-180";
  let t12;
  if ($[19] !== t11) {
    t12 = cn("size-3.5 transition-transform", t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <ChevronDown className={t12} />;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== label || $[24] !== open || $[25] !== t13) {
    t14 = <Button size="sm" variant="outline" aria-expanded={open} aria-haspopup="dialog" onClick={t9}>{t10}{label}{t13}</Button>;
    $[23] = label;
    $[24] = open;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== from || $[28] !== invalid || $[29] !== onChange || $[30] !== open || $[31] !== setFrom || $[32] !== shift || $[33] !== t || $[34] !== to || $[35] !== value.days || $[36] !== value.kind) {
    t15 = open && <div ref={panel} role="dialog" style={shift ? {
      transform: `translateX(${shift}px)`
    } : undefined} className="absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1rem)] rounded-md border bg-popover p-2 text-popover-foreground shadow-md"><div className="flex flex-col"><button type="button" onClick={() => {
          onChange({
            kind: "allTime"
          });
          setOpen(false);
        }} className={cn("flex items-center justify-between rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-muted", value.kind === "allTime" && "font-medium")}>{t("dashboard.range.allTime")}{value.kind === "allTime" && <Check className="size-4" />}</button><div className="my-1 border-t" />{DASHBOARD_PRESETS.map(days => {
          const active = value.kind === "preset" && value.days === days;
          return <button key={days} type="button" onClick={() => {
            onChange({
              kind: "preset",
              days
            });
            setOpen(false);
          }} className={cn("flex items-center justify-between rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-muted", active && "font-medium")}>{t("dashboard.range.days", {
              count: days
            })}{active && <Check className="size-4" />}</button>;
        })}</div><div className="mt-2 flex flex-col gap-2 border-t pt-2"><p className="px-2 text-xs font-medium text-muted-foreground">{t("dashboard.range.custom")}</p><div className="flex items-center gap-2 px-2"><Input type="date" aria-label={t("dashboard.range.from")} className="h-8 flex-1 px-2 text-xs" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} /><span className="text-xs text-muted-foreground">—</span><Input type="date" aria-label={t("dashboard.range.to")} className="h-8 flex-1 px-2 text-xs" value={to} min={from || undefined} onChange={e_0 => setTo(e_0.target.value)} /></div><Button size="sm" className="mx-2 mb-1" disabled={invalid} onClick={() => {
          onChange({
            kind: "custom",
            from,
            to
          });
          setOpen(false);
        }}>{t("dashboard.range.apply")}</Button></div></div>;
    $[27] = from;
    $[28] = invalid;
    $[29] = onChange;
    $[30] = open;
    $[31] = setFrom;
    $[32] = shift;
    $[33] = t;
    $[34] = to;
    $[35] = value.days;
    $[36] = value.kind;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div ref={container} className="relative">{t14}{t15}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}
function _temp(prev) {
  return !prev;
}